import type { ResetSettings } from '@brimstone/shared';
import { useState } from 'react';
import { EmptyState, Panel, SecondaryButton } from '../../components/ui';
import { useNow } from '../../lib/time';
import { useReset, useSaveReset } from '../../lib/tracking';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** The next reset under this schedule, in the browser's time zone: "Wednesday 05:00". */
function localReset(r: ResetSettings, now: number): string {
  // This week's occurrence: the offset depends on the date (summer time).
  const today = new Date(now);
  const at = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), r.hourUtc),
  );
  at.setUTCDate(at.getUTCDate() + ((r.weekday - at.getUTCDay() + 7) % 7));
  return at.toLocaleString('en-GB', { weekday: 'long', hour: '2-digit', minute: '2-digit' });
}

const select = 'min-h-11 border border-line bg-bg px-3 font-mono text-secondary';

/** The weekly reset (SPEC §6.11, A4): weeks for Playtime, the Chronicle and the digest. */
export function ResetPanel() {
  const stored = useReset();
  const save = useSaveReset();
  const now = useNow(3_600_000);
  const [draft, setDraft] = useState<ResetSettings | null>(null);
  const reset = draft ?? stored.data?.reset ?? null;
  const changed =
    reset &&
    stored.data &&
    (reset.weekday !== stored.data.reset.weekday || reset.hourUtc !== stored.data.reset.hourUtc);

  return (
    <Panel title="Weekly reset" level="side" meta="UTC">
      {!reset ? (
        <EmptyState>{stored.isError ? 'Couldn’t load it.' : 'Loading…'}</EmptyState>
      ) : (
        <div className="flex flex-col gap-3 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="Reset weekday"
              value={reset.weekday}
              onChange={(e) => setDraft({ ...reset, weekday: Number(e.target.value) })}
              className={select}
            >
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
            <select
              aria-label="Reset hour (UTC)"
              value={reset.hourUtc}
              onChange={(e) => setDraft({ ...reset, hourUtc: Number(e.target.value) })}
              className={select}
            >
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
          <p className="m-0 text-secondary text-muted">
            That is {localReset(reset, now)} your time. Weeks run from reset to reset; EU realms
            reset Wednesday 04:00 UTC (not yet confirmed for Forever).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <SecondaryButton
              type="button"
              disabled={!changed || save.isPending}
              onClick={() => save.mutate(reset, { onSuccess: () => setDraft(null) })}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </SecondaryButton>
            {stored.data && (
              <SecondaryButton
                type="button"
                disabled={save.isPending}
                onClick={() => setDraft(stored.data.defaults)}
              >
                Default
              </SecondaryButton>
            )}
            {save.isError && (
              <span className="text-secondary text-danger">
                Couldn’t save: {save.error.message}
              </span>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
